import { LoggerEventNames } from '@/lib/consts/logger';
import { RumEventLevel } from '@/lib/analytics/pendingRumEvents';
import coralogixRumService from '@/lib/services/coralogixRum';

/**
 * Every way the login flow can stop without producing a login and without
 * sending the player to /failed. These paths emit no publisher event and no
 * error, so before this they were indistinguishable from a player who simply
 * never arrived — which is exactly what publishers report as a vanished player.
 */
export enum LoginDeadEndReason {
    /** Shop rejected the session, no new credential to recover with. Stuck. */
    SESSION_EXPIRED_PURGE = 'session-expired-purge',
    /** Came back from Apple without the callback params. Stuck. */
    APPLE_REDIRECT_ABANDONED = 'apple-redirect-abandoned',
    /** Consumed handoff token replayed over a live session. Not stuck. */
    HANDOFF_REPLAY = 'handoff-replay',
}

interface DeadEndReporting {
    /** A replay still reaches the shop, so it must not inflate the error rate. */
    level: RumEventLevel;
    /**
     * True where reporting is followed by a hard navigation that destroys the
     * document before RUM can send anything.
     */
    crossesNavigation: boolean;
}

const REASON_REPORTING: Record<LoginDeadEndReason, DeadEndReporting> = {
    [LoginDeadEndReason.SESSION_EXPIRED_PURGE]: {
        level: RumEventLevel.ERROR,
        crossesNavigation: false,
    },
    [LoginDeadEndReason.APPLE_REDIRECT_ABANDONED]: {
        level: RumEventLevel.ERROR,
        crossesNavigation: false,
    },
    [LoginDeadEndReason.HANDOFF_REPLAY]: {
        level: RumEventLevel.INFO,
        crossesNavigation: true,
    },
};

export const reportLoginDeadEnd = (reason: LoginDeadEndReason): void => {
    const { level, crossesNavigation } = REASON_REPORTING[reason];
    const payload = { reason, pathname: readPathname() };

    if (crossesNavigation) {
        coralogixRumService.sendEventAcrossNavigation(
            level,
            LoggerEventNames.LOGIN_DEAD_END,
            payload
        );
        return;
    }

    if (level === RumEventLevel.INFO) {
        coralogixRumService.sendInfoEvent(LoggerEventNames.LOGIN_DEAD_END, payload);
        return;
    }
    coralogixRumService.sendErrorEvent(LoggerEventNames.LOGIN_DEAD_END, payload);
};

const readPathname = (): string =>
    typeof window === 'undefined' ? '' : window.location.pathname;
